// Bénévolat (D-026, D-034, D-038) : les besoins d'une même occasion — un match à
// domicile (une date), un événement du club, un appel à bénévoles (un ou plusieurs
// jours) — se lisent ensemble, jour par jour.

export type NeedSource = 'match' | 'event' | 'call';

export interface NeedLike {
  slot_id: string; event_id: string; occurrence_date: string; source: NeedSource;
  needed: number; taken: number;
}

export interface Occasion<T extends NeedLike> {
  key: string; source: NeedSource; first: T;
  days: { date: string; slots: T[] }[];
  needed: number; taken: number;
}

export const SOURCE_LABEL: Record<NeedSource, string> = { match: 'Match à domicile', event: 'Événement', call: 'Appel à bénévoles' };

/** Une occasion : un match se répète (une par date), un événement ou un appel est unique. */
export const occasionKey = (n: NeedLike) => (n.source === 'match' ? `match|${n.event_id}|${n.occurrence_date}` : `${n.source}|${n.event_id}`);

/** Places encore libres (jamais négatif). */
export const holes = (needs: NeedLike[]) => needs.reduce((a, n) => a + Math.max(0, n.needed - n.taken), 0);

/** Les besoins regroupés par occasion, dans l'ordre reçu (date, heure), puis par jour. */
export function occasions<T extends NeedLike>(needs: T[]): Occasion<T>[] {
  const map = new Map<string, Occasion<T>>();
  for (const n of needs) {
    const key = occasionKey(n);
    let o = map.get(key);
    if (!o) { o = { key, source: n.source, first: n, days: [], needed: 0, taken: 0 }; map.set(key, o); }
    let d = o.days.find(x => x.date === n.occurrence_date);
    if (!d) { d = { date: n.occurrence_date, slots: [] }; o.days.push(d); }
    d.slots.push(n);
    o.needed += n.needed;
    o.taken += Math.min(n.taken, n.needed);
  }
  return [...map.values()];
}

// -----------------------------------------------------------------------------
// Casquette Bénévole (D-097) : la pastille et la ligne « Le club a besoin de toi ».
// -----------------------------------------------------------------------------

/** Les créneaux que je peux prendre : pas complets, pas déjà à moi, pas réservés à un groupe dont je ne suis pas (D-065). */
export function openForMe<T extends NeedLike & { mine: boolean }>(needs: T[], reserved: Map<string, string>, myGroups: Set<string>): T[] {
  return needs.filter(n => !n.mine && n.taken < n.needed && (!reserved.get(n.slot_id) || myGroups.has(reserved.get(n.slot_id)!)));
}

/** « aujourd’hui », « demain », sinon le jour de la semaine (« samedi ») : la semaine qui vient. */
export function weekDay(date: string, today: string): string {
  const d = Math.round((Date.parse(`${date}T12:00:00`) - Date.parse(`${today}T12:00:00`)) / 86_400_000);
  return d <= 0 ? 'aujourd’hui' : d === 1 ? 'demain' : new Date(`${date}T12:00:00`).toLocaleDateString('fr-FR', { weekday: 'long' });
}

/**
 * La ligne des accueils hors casquette Bénévole, d'après les créneaux à prendre des 7 jours (dans l'ordre) :
 * un seul créneau, son nom (« Table de marque samedi · U13 – Torvigne · 1 place ») ; plusieurs, « Le club a
 * besoin de toi samedi · 4 places à prendre » (« dès samedi » s'ils tombent sur plusieurs jours). Rien : null.
 * Un seul chiffre partout (D-103) : les places encore libres des 7 jours, en « places ».
 */
export function gapLine<T extends NeedLike & { label: string; title: string }>(open: T[], today: string, vous = false): { lead: string; rest: string } | null {
  const first = open[0];
  if (!first) return null;
  const day = weekDay(first.occurrence_date, today);
  if (open.length === 1) {
    const left = first.needed - first.taken;
    return { lead: `${first.label.charAt(0).toUpperCase()}${first.label.slice(1)} ${day}`, rest: `${first.title} · ${left} ${left > 1 ? 'places' : 'place'}` };
  }
  const oneDay = open.every(n => n.occurrence_date === first.occurrence_date);
  const n = holes(open);
  return { lead: `Le club a besoin de ${vous ? 'vous' : 'toi'} ${oneDay ? '' : 'dès '}${day}`, rest: `${n} ${n > 1 ? 'places' : 'place'} à prendre` };
}

/** Le chiffre des places (D-103) : « 1 place », « 4 places ». */
export const placesLabel = (n: number) => `${n} ${n > 1 ? 'places' : 'place'}`;

// -----------------------------------------------------------------------------
// Créneaux en conflit (D-103) : ce qui occupe déjà le compte à l'heure d'un créneau (base :
// my_slot_conflicts). Ils sortent des propositions ; « Je prends » prévient d'abord.
// -----------------------------------------------------------------------------

/** Ce qui m'occupe sur un créneau : je joue (convoqué), j'entraîne l'équipe, je conduis, un autre créneau à moi. */
export interface ConflictLike {
  slot_id: string; kind: 'play' | 'coach' | 'drive' | 'slot'; event_kind: 'match' | 'training' | 'other' | null;
  team: string | null; opponent: string | null; away: boolean | null;
  starts_at: string; ends_at: string | null; meet_at: string | null; label: string | null;
}

/** Les créneaux sans conflit avec ce qui m'occupe. */
export const withoutConflicts = <T extends { slot_id: string }>(needs: T[], conflicts: { has(id: string): boolean }): T[] =>
  needs.filter(n => !conflicts.has(n.slot_id));

const hhmm = (iso: string) => new Date(iso).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Paris' }).replace(':', 'h');

/**
 * L'avertissement de « Je prends » : « Tu joues à cette heure-là » · « SF2 à Lusmagne, rendez-vous 11h10 ».
 * Un match : à l'extérieur « à », à domicile « contre » ; le rendez-vous quand la route est connue, sinon l'heure.
 */
export function conflictText(c: ConflictLike, vous = false): { lead: string; rest: string } {
  const v = (tu: string, vo: string) => (vous ? vo : tu);
  const at = `à ${hhmm(c.starts_at)}`;
  const span = `${hhmm(c.starts_at)}${c.ends_at ? ` – ${hhmm(c.ends_at)}` : ''}`;
  const match = `${c.team ?? ''}${c.opponent ? ` ${c.away ? 'à' : 'contre'} ${c.opponent}` : ''}`.trim();
  const when = c.meet_at ? `rendez-vous ${hhmm(c.meet_at)}` : at;
  if (c.kind === 'slot') return { lead: v('Tu as déjà un créneau à cette heure-là', 'Vous avez déjà un créneau à cette heure-là'), rest: `${c.label ?? 'Créneau'} · ${c.team ?? ''}, ${at}` };
  if (c.kind === 'drive') return { lead: v('Tu conduis à cette heure-là', 'Vous conduisez à cette heure-là'), rest: `${match}, ${when}` };
  if (c.event_kind === 'training') return { lead: v('Tu as entraînement à cette heure-là', 'Vous avez entraînement à cette heure-là'), rest: `${c.team ?? ''}, ${span}` };
  if (c.event_kind !== 'match') return { lead: v('Ton équipe a rendez-vous à cette heure-là', 'Votre équipe a rendez-vous à cette heure-là'), rest: `${c.team ?? ''}, ${at}` };
  if (c.kind === 'coach') return { lead: v('Tu coaches à cette heure-là', 'Vous coachez à cette heure-là'), rest: `${match}, ${when}` };
  return { lead: v('Tu joues à cette heure-là', 'Vous jouez à cette heure-là'), rest: `${match}, ${when}` };
}

/** Un créneau saisi par le bureau (heures « HH:MM », facultatives). */
/** `youth` : « Ouvert aux jeunes » (D-103), réglé après l'enregistrement de l'appel. */
export interface CallSlotDraft { id?: string; date: string; start: string; end: string; label: string; needed: number; youth?: boolean }

/** Ce qui empêche d'enregistrer un appel, ou null. */
export function callProblem(title: string, slots: CallSlotDraft[]): string | null {
  if (!title.trim()) return 'Donne un titre à l’appel.';
  if (!slots.length) return 'Ajoute au moins un créneau.';
  if (slots.length > 60) return '60 créneaux au plus.';
  for (const [i, s] of slots.entries()) {
    const n = `Créneau ${i + 1}`;
    if (!/^\d{4}-\d{2}-\d{2}$/.test(s.date)) return `${n} : choisis la date.`;
    if (!s.label.trim()) return `${n} : dis ce qu’il faut faire (ex. buvette).`;
    if (!Number.isInteger(s.needed) || s.needed < 1 || s.needed > 20) return `${n} : de 1 à 20 personnes.`;
    if (s.end && !s.start) return `${n} : indique l’heure de début.`;
    if (s.start && s.end && s.end <= s.start) return `${n} : la fin doit suivre le début.`;
  }
  return null;
}

/** Le créneau suivant, prérempli à partir du dernier : même jour, il commence quand l'autre finit. */
export function nextSlot(last: CallSlotDraft | undefined, today: string): CallSlotDraft {
  if (!last) return { date: today, start: '', end: '', label: '', needed: 1 };
  return { date: last.date, start: last.end || last.start, end: '', label: '', needed: 1 };
}

/** Les créneaux du dernier jour, recopiés le lendemain (tournoi sur deux jours). */
export function nextDay(slots: CallSlotDraft[], addDays: (d: string, n: number) => string): CallSlotDraft[] {
  const last = slots.reduce((m, s) => (s.date > m ? s.date : m), '');
  if (!last) return [];
  return slots.filter(s => s.date === last).map(s => ({ date: addDays(last, 1), start: s.start, end: s.end, label: s.label, needed: s.needed, youth: s.youth }));
}

// -----------------------------------------------------------------------------
// Niveaux d'avant D-100 (D-041) : seulement le type de la colonne perks.min_level, gardée jusqu'au nettoyage
// de la base. Les badges par parcours sont dans src/domain/badges.ts.
// -----------------------------------------------------------------------------
export type LevelKey = 'recrue' | 'renfort' | 'titulaire' | 'capitaine' | 'legende';
